/**
 * Chat service layer
 * Provides a clean abstraction over the IndexedDB chat database
 * with error handling, retry logic, and validation
 */

import { chatDB, type ChatMessage, type Conversation, type GlobalSettings, type ReasoningSettings, type WebSearchSettings } from '../lib/chat-db';
import type { ChatServiceInterface, ChatError } from '../types/chat';

class ChatService implements ChatServiceInterface {
  private static instance: ChatService;
  private initPromise: Promise<void> | null = null;

  private constructor() {}

  static getInstance(): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService();
    }
    return ChatService.instance;
  }

  /**
   * Initialize the database with retry logic
   */
  async initializeDatabase(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.retry(() => chatDB.init(), 3);
    return this.initPromise;
  }

  /**
   * Get all conversations sorted by update date
   */
  async getAllConversations(): Promise<Conversation[]> {
    try {
      await this.ensureInitialized();
      return await chatDB.getAllConversations();
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new conversation
   */
  async createConversation(conversationData: Omit<Conversation, 'id'>): Promise<Conversation> {
    try {
      await this.ensureInitialized();
      
      const conversation: Conversation = {
        id: crypto.randomUUID(),
        ...conversationData,
      };

      await chatDB.saveConversation(conversation);
      return conversation;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update an existing conversation
   */
  async updateConversation(id: string, updates: Partial<Conversation>): Promise<void> {
    try {
      await this.ensureInitialized();
      
      // Get existing conversation
      const conversations = await chatDB.getAllConversations();
      const existing = conversations.find(conv => conv.id === id);
      
      if (!existing) {
        throw new Error(`Conversation with id ${id} not found`);
      }

      const updated: Conversation = {
        ...existing,
        ...updates,
        updatedAt: new Date(),
      };

      await chatDB.saveConversation(updated);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a conversation and all its messages
   */
  async deleteConversation(id: string): Promise<void> {
    try {
      await this.ensureInitialized();
      await chatDB.deleteConversation(id);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update conversation title
   */
  async updateConversationTitle(id: string, title: string): Promise<void> {
    try {
      await this.ensureInitialized();
      await chatDB.updateConversationTitle(id, title);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get messages for a specific conversation
   */
  async getMessages(conversationId: string): Promise<ChatMessage[]> {
    try {
      await this.ensureInitialized();
      return await chatDB.getMessages(conversationId);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add a message to a conversation
   */
  async addMessage(message: ChatMessage): Promise<void> {
    try {
      await this.ensureInitialized();
      await chatDB.addMessage(message);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a message
   */
  async deleteMessage(messageId: string): Promise<void> {
    try {
      await this.ensureInitialized();
      await chatDB.deleteMessage(messageId);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get global settings
   */
  async getGlobalSettings(): Promise<GlobalSettings | null> {
    try {
      await this.ensureInitialized();
      const settings = await chatDB.getGlobalSettings();
      return settings || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Save global settings
   */
  async saveGlobalSettings(settings: Partial<GlobalSettings>): Promise<void> {
    try {
      await this.ensureInitialized();
      
      const existing = await chatDB.getGlobalSettings();
      const updated: GlobalSettings = {
        selectedModel: '',
        chatMemoryLimit: 20, // Default value
        lastUpdated: new Date(),
        ...existing,
        ...settings,
      };

      await chatDB.saveGlobalSettings(updated);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle and categorize errors
   */
  handleError(error: unknown): ChatError {
    console.error('Chat service error:', error);

    if (error instanceof Error) {
      // Database errors
      if (error.message.includes('IndexedDB') || error.message.includes('database')) {
        return {
          type: 'database',
          message: 'Failed to access local storage. Please refresh and try again.',
          code: 'DB_ERROR'
        };
      }

      // Network errors
      if (error.message.includes('fetch') || error.message.includes('network')) {
        return {
          type: 'network',
          message: 'Network error. Please check your connection and try again.',
          code: 'NETWORK_ERROR'
        };
      }

      // Rate limit errors
      if (error.message.includes('rate limit') || error.message.includes('429')) {
        return {
          type: 'rate_limit',
          message: 'Rate limit exceeded. Please wait before trying again.',
          code: 'RATE_LIMIT'
        };
      }

      // Model not found errors
      if (error.message.includes('model') && error.message.includes('not found')) {
        return {
          type: 'model_not_found',
          message: 'Selected model is not available. Please choose a different model.',
          code: 'MODEL_NOT_FOUND'
        };
      }

      return {
        type: 'unknown',
        message: error.message || 'An unexpected error occurred.',
        code: 'UNKNOWN_ERROR'
      };
    }

    return {
      type: 'unknown',
      message: 'An unexpected error occurred. Please try again.',
      code: 'UNKNOWN_ERROR'
    };
  }

  /**
   * Retry an operation with exponential backoff
   */
  async retry<T>(
    operation: () => Promise<T>, 
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        if (attempt === maxRetries) {
          break;
        }

        // Exponential backoff with jitter
        const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  }

  /**
   * Ensure database is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.initPromise) {
      await this.initializeDatabase();
    } else {
      await this.initPromise;
    }
  }

  /**
   * Get model-specific reasoning preferences
   */
  async getModelPreferences(modelId: string): Promise<ReasoningSettings | null> {
    try {
      await this.ensureInitialized();
      const preferences = await chatDB.getModelPreferences(modelId);
      return preferences || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Save model-specific reasoning preferences
   */
  async saveModelPreferences(modelId: string, settings: ReasoningSettings): Promise<void> {
    try {
      await this.ensureInitialized();
      await chatDB.saveModelPreferences(modelId, settings);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete model-specific reasoning preferences
   */
  async deleteModelPreferences(modelId: string): Promise<void> {
    try {
      await this.ensureInitialized();
      await chatDB.deleteModelPreferences(modelId);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get model-specific web search preferences
   */
  async getWebSearchPreferences(modelId: string): Promise<WebSearchSettings | null> {
    try {
      await this.ensureInitialized();
      const preferences = await chatDB.getWebSearchPreferences(modelId);
      return preferences || null;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Save model-specific web search preferences
   */
  async saveWebSearchPreferences(modelId: string, settings: WebSearchSettings): Promise<void> {
    try {
      await this.ensureInitialized();
      await chatDB.saveWebSearchPreferences(modelId, settings);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Clear all data (for testing or reset)
   */
  async clearAllData(): Promise<void> {
    try {
      await this.ensureInitialized();
      const conversations = await this.getAllConversations();
      
      // Delete all conversations (this will also delete messages)
      await Promise.all(
        conversations.map(conv => this.deleteConversation(conv.id))
      );
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get database statistics
   */
  async getStatistics(): Promise<{
    conversationCount: number;
    messageCount: number;
    oldestConversation: Date | null;
    newestConversation: Date | null;
  }> {
    try {
      await this.ensureInitialized();
      const conversations = await this.getAllConversations();
      
      let totalMessages = 0;
      let oldestDate: Date | null = null;
      let newestDate: Date | null = null;

      for (const conv of conversations) {
        totalMessages += conv.messageCount;
        
        if (!oldestDate || conv.createdAt < oldestDate) {
          oldestDate = conv.createdAt;
        }
        
        if (!newestDate || conv.updatedAt > newestDate) {
          newestDate = conv.updatedAt;
        }
      }

      return {
        conversationCount: conversations.length,
        messageCount: totalMessages,
        oldestConversation: oldestDate,
        newestConversation: newestDate,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get chat memory limit setting
   */
  async getChatMemoryLimit(): Promise<number> {
    try {
      await this.ensureInitialized();
      const settings = await chatDB.getGlobalSettings();
      return settings?.chatMemoryLimit ?? 20; // Default to 20 if not set
    } catch (error) {
      console.error('Failed to get chat memory limit:', error);
      return 20; // Default fallback
    }
  }

  /**
   * Save chat memory limit setting
   */
  async saveChatMemoryLimit(limit: number): Promise<void> {
    try {
      await this.ensureInitialized();
      
      const existing = await chatDB.getGlobalSettings();
      const updated: GlobalSettings = {
        selectedModel: existing?.selectedModel || '',
        selectedCharacter: existing?.selectedCharacter,
        customPrompt: existing?.customPrompt,
        chatMemoryLimit: limit,
        lastUpdated: new Date(),
      };

      await chatDB.saveGlobalSettings(updated);
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// Export singleton instance
export const chatService = ChatService.getInstance();
export type { ChatServiceInterface };